/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Load More & View All for collection pages
document.addEventListener('DOMContentLoaded', function () {
  const productFacet = document.querySelector('product-facet');
  if (!productFacet) return;

  const productListInner = document.querySelector('.product-list__inner');
  if (!productListInner) return;
  productListInner.classList.add('smarte-load-context');

  // If a legacy pagination parameter is present, normalize to page=1 and reload once
  try {
    const current = new URL(window.location.href);
    if (current.searchParams.has('page')) {
      current.searchParams.delete('page');
      history.replaceState({}, '', current.pathname + current.search);
      // Reload so Liquid renders page 1; after this, Load More takes over.
      window.location.reload();
      return;
    }
  } catch (_err) {
    // no-op; safest behavior is to continue without URL normalization
  }

  const sectionId = productFacet.getAttribute('section-id');
  function getWrap() { return productFacet.querySelector('.smarte-load-more'); }
  function getListInner() { return productFacet.querySelector('.product-list__inner'); }
  function getStatusEl() { const w = getWrap(); return w ? w.querySelector('.smarte-load-more__count') : null; }
  function getBar() { const w = getWrap(); return w ? w.querySelector('.smarte-load-more__bar') : null; }
  function getBarInner() { const w = getWrap(); return w ? w.querySelector('.smarte-load-more__bar-inner') : null; }
  function getTotal() { const w = getWrap(); return w ? Number(w.getAttribute('data-total')) || 0 : 0; }
  // Standard cards are <product-item>; the Christmas grid (main-collection-xmas) uses <article class="xmas-card">
  const PRODUCT_SELECTOR = 'product-item, .xmas-card';
  function isProductNode(node) { return node.nodeType === 1 && node.matches(PRODUCT_SELECTOR + ', .product-item'); }
  function getViewedCount() { const list = getListInner(); return list ? list.querySelectorAll(PRODUCT_SELECTOR).length : 0; }

  // Get review blocks data from JSON script
  function getReviewBlocksData() {
    const scriptEl = document.getElementById('review-blocks-data-' + sectionId);
    if (scriptEl) {
      try {
        return JSON.parse(scriptEl.textContent);
      } catch (e) {
        console.error('Failed to parse review blocks data', e);
      }
    }
    return { blocks: [] };
  }

  // Generate star rating HTML
  function generateStars(rating, uniqueId) {
    const fullStars = Math.floor(rating);
    const hasHalfStar = (rating % 1) >= 0.3 && (rating % 1) < 0.8;
    const isRoundedUp = (rating % 1) >= 0.8;
    const actualFullStars = isRoundedUp ? fullStars + 1 : fullStars;
    const gradientId = 'half-star-gradient-' + (uniqueId || Date.now() + Math.random());

    let starsHTML = '';
    for (let i = 1; i <= 5; i++) {
      if (i <= actualFullStars) {
        starsHTML += '<svg class="review-star review-star--full" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="18" height="18" y="0.667" fill="currentColor"/><path d="M9 13.3431L12.0417 12.5286L13.3125 16.667L9 13.3431ZM16 7.99404H10.6458L9 2.66699L7.35417 7.99404H2L6.33334 11.2959L4.6875 16.623L9.02083 13.3211L11.6875 11.2959L16 7.99404Z" fill="white"/></svg>';
      } else if (i === actualFullStars + 1 && hasHalfStar) {
        starsHTML += '<svg class="review-star review-star--half" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="18" height="18" y="0.667" fill="url(#' + gradientId + ')"/><path d="M9 13.3431L12.0417 12.5286L13.3125 16.667L9 13.3431ZM16 7.99404H10.6458L9 2.66699L7.35417 7.99404H2L6.33334 11.2959L4.6875 16.623L9.02083 13.3211L11.6875 11.2959L16 7.99404Z" fill="white"/><defs><linearGradient id="' + gradientId + '" x1="0" y1="9" x2="18" y2="9" gradientUnits="userSpaceOnUse"><stop stop-color="currentColor"/><stop offset="0.5" stop-color="currentColor"/><stop offset="0.5001" stop-color="currentColor" stop-opacity="0"/><stop offset="0.5002" stop-color="#D9D9D9"/><stop offset="1" stop-color="#D9D9D9"/></linearGradient></defs></svg>';
      } else {
        starsHTML += '<svg class="review-star review-star--empty" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="18" height="18" y="0.667" fill="#D9D9D9"/><path d="M9 13.3431L12.0417 12.5286L13.3125 16.667L9 13.3431ZM16 7.99404H10.6458L9 2.66699L7.35417 7.99404H2L6.33334 11.2959L4.6875 16.623L9.02083 13.3211L11.6875 11.2959L16 7.99404Z" fill="white"/></svg>';
      }
    }
    return starsHTML;
  }

  // Generate review block HTML
  function generateReviewBlock(blockData) {
    const uniqueId = blockData.blockId || Date.now();
    const trustpilotLogo = blockData.showTrustpilotLogo ? `<div class="review-block__trustpilot-logo"><svg xmlns="http://www.w3.org/2000/svg" width="75" height="18" viewBox="0 0 75 18" fill="none">
<path d="M26.7532 6.33536V7.71038H23.8275V15.44H22.2187V7.71038H19.3059V6.33536H26.7532ZM27.8106 8.84766V10.1199H27.8366C27.882 9.93996 27.9663 9.76648 28.0896 9.59942C28.2128 9.43236 28.362 9.27173 28.5372 9.1368C28.7124 8.99544 28.907 8.88621 29.121 8.79626C29.3351 8.71273 29.5557 8.66775 29.7763 8.66775C29.9449 8.66775 30.0682 8.67418 30.1331 8.6806C30.1979 8.68703 30.2628 8.69988 30.3342 8.7063V10.107C30.2304 10.0877 30.1266 10.0749 30.0163 10.062C29.906 10.0492 29.8022 10.0428 29.6984 10.0428C29.4519 10.0428 29.2184 10.0942 28.9978 10.1905C28.7772 10.2869 28.5891 10.4347 28.4269 10.621C28.2647 10.8138 28.135 11.0451 28.0377 11.3278C27.9404 11.6105 27.895 11.9318 27.895 12.2981V15.4336H26.4289V8.84766H27.8106ZM37.0744 15.44H35.6342V14.5212H35.6083C35.4266 14.8553 35.1606 15.1188 34.8038 15.3179C34.447 15.5171 34.0838 15.6199 33.714 15.6199C32.8382 15.6199 32.2025 15.4079 31.8132 14.9774C31.424 14.5469 31.2294 13.898 31.2294 13.0305V8.84766H32.6955V12.8892C32.6955 13.4675 32.8058 13.8787 33.0328 14.1164C33.2534 14.3542 33.5713 14.4762 33.9735 14.4762C34.2849 14.4762 34.5379 14.4313 34.7454 14.3349C34.953 14.2385 35.1217 14.1164 35.245 13.9558C35.3747 13.8016 35.4655 13.6088 35.5239 13.3904C35.5823 13.1719 35.6083 12.9342 35.6083 12.6771V8.85408H37.0744V15.44ZM39.5719 13.3261C39.6173 13.7502 39.7795 14.0457 40.0585 14.2192C40.3439 14.3863 40.6812 14.4762 41.077 14.4762C41.2132 14.4762 41.3689 14.4634 41.544 14.4441C41.7192 14.4248 41.8879 14.3799 42.0371 14.322C42.1928 14.2642 42.316 14.1742 42.4198 14.0586C42.5171 13.9429 42.5625 13.7951 42.556 13.6088C42.5496 13.4225 42.4782 13.2683 42.3484 13.1526C42.2187 13.0305 42.0565 12.9406 41.8554 12.8635C41.6543 12.7928 41.4273 12.7285 41.1678 12.6771C40.9083 12.6257 40.6488 12.5679 40.3828 12.5101C40.1104 12.4523 39.8444 12.3752 39.5914 12.2916C39.3384 12.2081 39.1113 12.0924 38.9102 11.9447C38.7091 11.8033 38.547 11.617 38.4302 11.3921C38.3069 11.1672 38.2485 10.8909 38.2485 10.5568C38.2485 10.197 38.3394 9.90141 38.5145 9.65725C38.6897 9.41308 38.9167 9.22033 39.1827 9.07254C39.4552 8.92476 39.7536 8.82196 40.0844 8.7577C40.4153 8.69988 40.7331 8.66775 41.0316 8.66775C41.3754 8.66775 41.7062 8.7063 42.0176 8.77698C42.329 8.84766 42.6144 8.96331 42.8674 9.13037C43.1204 9.29101 43.328 9.50304 43.4967 9.76005C43.6654 10.0171 43.7692 10.3319 43.8146 10.6981H42.2836C42.2122 10.3512 42.0565 10.1134 41.8035 9.99779C41.5505 9.87571 41.2586 9.81788 40.9342 9.81788C40.8304 9.81788 40.7072 9.8243 40.5645 9.84358C40.4218 9.86286 40.292 9.89498 40.1623 9.93996C40.039 9.98494 39.9352 10.0556 39.8444 10.1456C39.7601 10.2355 39.7146 10.3512 39.7146 10.499C39.7146 10.6789 39.7795 10.8202 39.9028 10.9295C40.026 11.0387 40.1882 11.1286 40.3893 11.2057C40.5904 11.2764 40.8175 11.3407 41.077 11.3921C41.3364 11.4435 41.6024 11.5013 41.8749 11.5591C42.1409 11.617 42.4003 11.6941 42.6598 11.7776C42.9193 11.8611 43.1464 11.9768 43.3475 12.1246C43.5486 12.2724 43.7108 12.4523 43.834 12.6707C43.9573 12.8892 44.0221 13.1655 44.0221 13.4867C44.0221 13.8787 43.9313 14.2064 43.7497 14.4827C43.568 14.7525 43.3345 14.9774 43.0491 15.1445C42.7636 15.3115 42.4393 15.44 42.089 15.5171C41.7387 15.5942 41.3883 15.6328 41.0445 15.6328C40.6229 15.6328 40.2336 15.5878 39.8768 15.4914C39.52 15.3951 39.2086 15.2537 38.9492 15.0674C38.6897 14.8746 38.4821 14.6369 38.3329 14.3542C38.1837 14.0714 38.1058 13.7309 38.0928 13.339H39.5719V13.3261ZM44.4114 8.84766H45.5207V6.86866H46.9868V8.84766H48.3102V9.93354H46.9868V13.4546C46.9868 13.6088 46.9933 13.7373 47.0063 13.853C47.0192 13.9622 47.0517 14.0586 47.0971 14.1357C47.1425 14.2128 47.2139 14.2706 47.3112 14.3092C47.4085 14.3477 47.5317 14.367 47.7004 14.367C47.8042 14.367 47.908 14.367 48.0118 14.3606C48.1156 14.3542 48.2194 14.3413 48.3232 14.3156V15.44C48.161 15.4593 47.9988 15.4722 47.8496 15.4914C47.6939 15.5107 47.5382 15.5171 47.376 15.5171C46.9868 15.5171 46.6754 15.4786 46.4419 15.4079C46.2083 15.3372 46.0202 15.228 45.8905 15.0866C45.7542 14.9453 45.6699 14.7718 45.618 14.5598C45.5726 14.3477 45.5402 14.1036 45.5337 13.8337V9.94639H44.4244V8.84766H44.4114ZM49.3481 8.84766H50.7364V9.74078H50.7624C50.9699 9.35526 51.2554 9.08539 51.6251 8.91834C51.9949 8.75128 52.3906 8.66775 52.8253 8.66775C53.3507 8.66775 53.8049 8.7577 54.1941 8.94404C54.5833 9.12395 54.9077 9.37453 55.1672 9.6958C55.4267 10.0171 55.6148 10.3897 55.7445 10.8138C55.8743 11.2379 55.9391 11.6941 55.9391 12.176C55.9391 12.6193 55.8808 13.0498 55.764 13.461C55.6472 13.8787 55.4721 14.2449 55.2385 14.5662C55.005 14.8875 54.7066 15.138 54.3433 15.3308C53.98 15.5236 53.5583 15.6199 53.0653 15.6199C52.8512 15.6199 52.6372 15.6007 52.4231 15.5621C52.209 15.5236 52.0014 15.4593 51.8068 15.3758C51.6122 15.2922 51.424 15.183 51.2619 15.0481C51.0932 14.9132 50.957 14.7589 50.8402 14.5855H50.8143V17.8752H49.3481V8.84766ZM54.473 12.1503C54.473 11.8547 54.4341 11.5656 54.3563 11.2829C54.2784 11.0001 54.1616 10.756 54.006 10.5375C53.8503 10.3191 53.6556 10.1456 53.4286 10.0171C53.1951 9.88856 52.9291 9.81788 52.6307 9.81788C52.0144 9.81788 51.5473 10.0299 51.2359 10.454C50.9245 10.8781 50.7688 11.4435 50.7688 12.1503C50.7688 12.4844 50.8078 12.7928 50.8921 13.0755C50.9764 13.3582 51.0932 13.6024 51.2619 13.808C51.424 14.0136 51.6187 14.1742 51.8457 14.2899C52.0728 14.412 52.3387 14.4698 52.6372 14.4698C52.9745 14.4698 53.2534 14.3991 53.487 14.2642C53.7205 14.1293 53.9086 13.9494 54.0578 13.7373C54.2071 13.5189 54.3173 13.2747 54.3822 12.9984C54.4406 12.7221 54.473 12.4394 54.473 12.1503ZM57.0614 6.33536H58.5275V7.71038H57.0614V6.33536ZM57.0614 8.84766H58.5275V15.44H57.0614V8.84766ZM59.838 6.33536H61.3041V15.44H59.838V6.33536ZM65.7997 15.6199C65.2677 15.6199 64.7942 15.53 64.379 15.3565C63.9638 15.183 63.6135 14.9389 63.3216 14.6369C63.0361 14.3285 62.8156 13.9622 62.6664 13.5381C62.5172 13.1141 62.4393 12.645 62.4393 12.1374C62.4393 11.6362 62.5172 11.1736 62.6664 10.7496C62.8156 10.3255 63.0361 9.95924 63.3216 9.65082C63.607 9.34241 63.9638 9.10467 64.379 8.93119C64.7942 8.7577 65.2677 8.66775 65.7997 8.66775C66.3316 8.66775 66.8052 8.7577 67.2204 8.93119C67.6356 9.10467 67.9859 9.34883 68.2778 9.65082C68.5632 9.95924 68.7838 10.3255 68.933 10.7496C69.0822 11.1736 69.1601 11.6362 69.1601 12.1374C69.1601 12.645 69.0822 13.1141 68.933 13.5381C68.7838 13.9622 68.5632 14.3285 68.2778 14.6369C67.9924 14.9453 67.6356 15.183 67.2204 15.3565C66.8052 15.53 66.3316 15.6199 65.7997 15.6199ZM65.7997 14.4698C66.124 14.4698 66.4095 14.3991 66.6495 14.2642C66.8895 14.1293 67.0842 13.9494 67.2398 13.7309C67.3955 13.5124 67.5058 13.2618 67.5837 12.9856C67.655 12.7093 67.6939 12.4266 67.6939 12.1374C67.6939 11.8547 67.655 11.5784 67.5837 11.2957C67.5123 11.013 67.3955 10.7688 67.2398 10.5504C67.0842 10.3319 66.8895 10.1584 66.6495 10.0235C66.4095 9.88856 66.124 9.81788 65.7997 9.81788C65.4753 9.81788 65.1899 9.88856 64.9499 10.0235C64.7098 10.1584 64.5152 10.3383 64.3595 10.5504C64.2038 10.7688 64.0936 11.013 64.0157 11.2957C63.9443 11.5784 63.9054 11.8547 63.9054 12.1374C63.9054 12.4266 63.9443 12.7093 64.0157 12.9856C64.0871 13.2618 64.2038 13.5124 64.3595 13.7309C64.5152 13.9494 64.7098 14.1293 64.9499 14.2642C65.1899 14.4056 65.4753 14.4698 65.7997 14.4698ZM69.5882 8.84766H70.6975V6.86866H72.1636V8.84766H73.487V9.93354H72.1636V13.4546C72.1636 13.6088 72.1701 13.7373 72.1831 13.853C72.1961 13.9622 72.2285 14.0586 72.2739 14.1357C72.3193 14.2128 72.3907 14.2706 72.488 14.3092C72.5853 14.3477 72.7086 14.367 72.8772 14.367C72.981 14.367 73.0848 14.367 73.1886 14.3606C73.2924 14.3542 73.3962 14.3413 73.5 14.3156V15.44C73.3378 15.4593 73.1756 15.4722 73.0264 15.4914C72.8707 15.5107 72.715 15.5171 72.5529 15.5171C72.1636 15.5171 71.8522 15.4786 71.6187 15.4079C71.3852 15.3372 71.197 15.228 71.0673 15.0866C70.9311 14.9453 70.8467 14.7718 70.7948 14.5598C70.7494 14.3477 70.717 14.1036 70.7105 13.8337V9.94639H69.6012V8.84766H69.5882Z" fill="#29404A"/>
  <path d="M17.5998 6.33536H10.879L8.80313 0L6.72074 6.33536L0 6.32893L5.44276 10.2484L3.36037 16.5773L8.80313 12.6643L14.2394 16.5773L12.1635 10.2484L17.5998 6.33536Z" fill="#00B67A"/>
  <path d="M12.6306 11.6812L12.1635 10.2484L8.80313 12.6643L12.6306 11.6812Z" fill="#005128"/>
</svg></div>` : '';
    const reviewText = blockData.reviewText || '';
    const reviewerName = blockData.reviewerName || '';
    const buttonHTML = blockData.buttonText ? `<div class="review-block__button-wrapper"><a href="${blockData.buttonUrl || '#'}" class="review-block__button button button--primary">${blockData.buttonText}</a></div>` : '';

    return `
      <div class="review-block-wrapper" id="block-${blockData.sectionId}-${blockData.blockId}" style="--review-bg-color: ${blockData.backgroundColor}; --review-text-color: ${blockData.textColor}; --review-rating-color: ${blockData.ratingColor};">
        <div class="review-block">
          <div class="review-block__top-rating">
            <div class="review-block__rating-left">
              <span class="review-block__rating-label">Excellent</span>
              <div class="review-stars review-block__top-stars" style="color: ${blockData.ratingColor};">
                ${generateStars(blockData.topRating, uniqueId + '-top')}
              </div>
              <span class="review-block__rating-text">${blockData.topRating}/5 based on ${blockData.topReviewsCount} reviews</span>
            </div>
            ${trustpilotLogo}
          </div>
          <div class="review-block__stars">
            <div class="review-stars review-block__second-stars" style="color: ${blockData.ratingColor};">
              ${generateStars(blockData.secondRating, uniqueId + '-second')}
            </div>
          </div>
          ${reviewText ? `<div class="review-block__text">${reviewText}</div>` : ''}
          ${reviewerName ? `<div class="review-block__reviewer">${reviewerName}</div>` : ''}
          ${buttonHTML}
        </div>
      </div>
    `;
  }

  function hideWrap() { const w = getWrap(); if (w) w.style.display = 'none'; }
  function showWrap() { const w = getWrap(); if (w) w.style.display = ''; }

  function updateStatus() {
    const viewed = getViewedCount();
    const total = getTotal();
    const statusEl = getStatusEl();
    const bar = getBar();
    const barInner = getBarInner();
    if (statusEl) statusEl.textContent = `You've viewed ${viewed} of ${total} Products`;
    const inline = productFacet.querySelector('.smarte-inline-count');
    if (inline) inline.textContent = `${viewed} of ${total} Products`;
    if (bar && barInner) {
      const pct = total > 0 ? Math.min(100, Math.round((viewed / total) * 100)) : 0;
      bar.setAttribute('aria-valuenow', String(viewed));
      barInner.style.width = pct + '%';
    }

    // Hide or show the Load More UI based on progress
    if (viewed >= total) hideWrap(); else showWrap();
  }

  function getNextUrl() { const w = getWrap(); return w ? w.getAttribute('data-next-url') || '' : ''; }

  function setNextUrl(nextUrl) { const w = getWrap(); if (!w) return; if (nextUrl) w.setAttribute('data-next-url', nextUrl); else w.removeAttribute('data-next-url'); }

  async function fetchSection(url) {
    try {
      const full = new URL(url, window.location.origin);
      full.searchParams.set('section_id', sectionId);
      const res = await fetch(full.toString(), { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Network response was not ok');
      const html = await res.text();
      const div = document.createElement('div');
      div.innerHTML = html;
      return div;
    } catch (_err) {
      return document.createElement('div');
    }
  }

  async function loadNextPage() {
    const next = getNextUrl();
    if (!next) return;
    try {
      const div = await fetchSection(next);
      const incomingInner = div.querySelector('.product-list__inner');
      const incomingLoadMore = div.querySelector('.smarte-load-more');
      if (incomingInner) {
        const list = getListInner();
        if (!list) return;

        // Get current product count before adding new ones
        const currentProductCount = list.querySelectorAll(PRODUCT_SELECTOR).length;

        // Get review blocks metadata
        const wrap = getWrap();
        const collectionCount = wrap ? Number(wrap.getAttribute('data-collection-count')) || 0 : 0;
        const reviewBlocksConfig = getReviewBlocksData();

        // Append items maintaining server order, and track product count
        let productIndex = currentProductCount;
        const allIncomingItems = Array.from(incomingInner.children);

        allIncomingItems.forEach((node) => {
          // Check if it's a product item
          if (isProductNode(node)) {
            productIndex++;
            node.removeAttribute('reveal');
            node.style.opacity = '1';
            list.appendChild(node);

            // After appending a product, check if a review block should appear here
            reviewBlocksConfig.blocks.forEach(reviewBlock => {
              if (reviewBlock.position === productIndex && collectionCount > reviewBlock.position) {
                const existingReview = list.querySelector(`[id*="${reviewBlock.blockId}"]`);
                if (!existingReview) {
                  // First, try to find it in the incoming HTML
                  const incomingReview = Array.from(incomingInner.children).find(child => {
                    if (child.classList.contains('review-block-wrapper')) {
                      const childId = child.id || '';
                      return childId.includes(reviewBlock.blockId);
                    }
                    return false;
                  });

                  if (incomingReview) {
                    // Use the one from incoming HTML
                    const reviewClone = incomingReview.cloneNode(true);
                    list.appendChild(reviewClone);
                  } else {
                    // Generate it dynamically
                    const reviewHTML = generateReviewBlock(reviewBlock);
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = reviewHTML.trim();
                    const reviewElement = tempDiv.firstElementChild;
                    if (reviewElement) {
                      list.appendChild(reviewElement);
                    }
                  }
                }
              }
            });
          } else if (node.classList.contains('review-block-wrapper')) {
            // This is a review block from the server - check if it should be added
            const blockId = (node.id || '').split('-').pop();
            const matchingReviewBlock = reviewBlocksConfig.blocks.find(rb => String(rb.blockId) === String(blockId));
            if (matchingReviewBlock) {
              // Check if this review block should appear at the current position
              if (matchingReviewBlock.position === productIndex && collectionCount > matchingReviewBlock.position) {
                const existingReview = list.querySelector(`[id*="${matchingReviewBlock.blockId}"]`);
                if (!existingReview) {
                  list.appendChild(node.cloneNode(true));
                }
              }
            }
          } else {
            // Other items - just append them
            list.appendChild(node);
          }
        });

        updateStatus();
      }
      if (incomingLoadMore) {
        const newNext = incomingLoadMore.getAttribute('data-next-url') || '';
        setNextUrl(newNext);
        if (!newNext) hideWrap(); else showWrap();
      } else {
        hideWrap();
      }
    } catch (_err) {
      // On any error, stop showing load more to avoid a broken state
      hideWrap();
    }

    // Do NOT change the URL on load more; we keep the current page so a reload preserves all items shown
  }

  function hasMore() { return !!getNextUrl(); }

  // Delegate clicks so it survives facet rerenders
  productFacet.addEventListener('click', async function (e) {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    const lm = target.closest('[data-action="load-more"]');
    const va = target.closest('[data-action="view-all"]');
    if (!lm && !va) return;
    e.preventDefault();
    if (lm) {
      const btn = lm;
      btn.disabled = true;
      await loadNextPage();
      if (hasMore()) btn.disabled = false;
    } else if (va) {
      // Best-effort: load until no more pages
      while (hasMore()) { await loadNextPage(); }
    }
  });

  // Initialize status on first render
  updateStatus();

  // Update after AJAX rerenders of the facet content (guarded & throttled via rAF)
  const facetMain = document.querySelector('#facet-main');
  if (facetMain) {
    let scheduled = false;
    const mo = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        // Intentionally re-query using document.querySelector per requirement
        if (!document.querySelector('.smarte-load-context')) {
          const currentList = document.querySelector('.product-list__inner');
          if (currentList) currentList.classList.add('smarte-load-context');
          updateStatus();
        }
      });
    });
    mo.observe(facetMain, { childList: true, subtree: true });
  }
});

// The desktop menu is hidden below 1200px. Coming back up, close the mobile drawer so it
// doesn't stay on screen at its narrow width over the wide menu.
(() => {
  const desktop = window.matchMedia('(min-width: 1200px)');
  const restore = () => {
    if (!desktop.matches) return;
    document.querySelectorAll('mobile-navigation').forEach((drawer) => {
      if (drawer.open) drawer.open = false;
    });
  };
  if (desktop.addEventListener) desktop.addEventListener('change', restore);
  else desktop.addListener(restore);
})();

// Navigation offer card countdown (snippets/nav-mega-offer.liquid). The page HTML may be cached,
// so the days left are recounted in the visitor's time zone, the same way as the Liquid.
(() => {
  const updateNavCountdowns = () => {
    document.querySelectorAll('[data-nav-deadline]').forEach((el) => {
      const [y, m, d] = el.dataset.navDeadline.split('-').map(Number);
      if (!y || !m || !d) return;
      const days = Math.floor((new Date(y, m - 1, d, 23, 59, 59) - new Date()) / 86400000);
      if (days < 0) {
        el.hidden = true;
        return;
      }
      const template = el.dataset.navDaysText || '[days] days left';
      el.textContent = days === 0
        ? el.dataset.navLastText || 'Last day to order'
        : days === 1
          ? template.replace('[days] days', '1 day').replace('[days]', '1')
          : template.replace('[days]', String(days));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateNavCountdowns);
  } else {
    updateNavCountdowns();
  }
  document.addEventListener('shopify:section:load', updateNavCountdowns);
})();